export const THEME_STORAGE_KEY = "elevenx.theme.preference";
export const THEME_CHANGE_EVENT = "elevenx.theme.change.v2";

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;

export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type ResolvedTheme = "light" | "dark";
export type ThemeSnapshot = `${ThemePreference}:${ResolvedTheme}`;

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function resolveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): ResolvedTheme {
  return preference === "system"
    ? systemPrefersDark ? "dark" : "light"
    : preference;
}

export function readThemePreference(targetWindow: Window): ThemePreference {
  try {
    const stored = targetWindow.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark(targetWindow: Window): boolean {
  return targetWindow.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function applyTheme(
  targetWindow: Window,
  preference: ThemePreference,
): ResolvedTheme {
  const resolvedTheme = resolveTheme(preference, systemPrefersDark(targetWindow));
  const root = targetWindow.document.documentElement;
  root.dataset.themePreference = preference;
  root.dataset.theme = resolvedTheme;
  root.style.colorScheme = resolvedTheme;
  const event = targetWindow.document.createEvent("Event");
  event.initEvent(THEME_CHANGE_EVENT, false, false);
  targetWindow.dispatchEvent(event);
  return resolvedTheme;
}

export function setThemePreference(
  targetWindow: Window,
  preference: ThemePreference,
): ResolvedTheme {
  try {
    targetWindow.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // The UI still follows the selected appearance for this document.
  }
  return applyTheme(targetWindow, preference);
}

export function getThemeSnapshot(targetWindow: Window): ThemeSnapshot {
  const root = targetWindow.document.documentElement;
  const preference = isThemePreference(root.dataset.themePreference)
    ? root.dataset.themePreference
    : readThemePreference(targetWindow);
  const resolvedTheme = root.dataset.theme === "light" || root.dataset.theme === "dark"
    ? root.dataset.theme
    : resolveTheme(preference, systemPrefersDark(targetWindow));
  return `${preference}:${resolvedTheme}`;
}

export function installThemeRuntime(targetWindow: Window): () => void {
  const media = targetWindow.matchMedia("(prefers-color-scheme: dark)");
  const handleSystemChange = () => {
    const preference = readThemePreference(targetWindow);
    if (preference === "system") applyTheme(targetWindow, preference);
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY) return;
    applyTheme(
      targetWindow,
      isThemePreference(event.newValue) ? event.newValue : "system",
    );
  };

  media.addEventListener("change", handleSystemChange);
  targetWindow.addEventListener("storage", handleStorage);
  applyTheme(targetWindow, readThemePreference(targetWindow));

  return () => {
    media.removeEventListener("change", handleSystemChange);
    targetWindow.removeEventListener("storage", handleStorage);
  };
}
